import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle2, AlertCircle, Mail, UserPlus, LogIn } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AcceptInvitePage() {
  const { token: inviteToken } = useParams();
  const navigate = useNavigate();
  const { user, token, isAuthenticated } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [coResult, setCoResult] = useState(null);
  const [blast, setBlast] = useState({ emails: '', note: '', sending: false, sent: null });

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/team/invites/accept/${inviteToken}`);
        setInvite(res.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Invite not found');
      } finally {
        setLoading(false);
      }
    })();
  }, [inviteToken]);

  const accept = useCallback(async () => {
    setAccepting(true);
    try {
      const res = await axios.post(`${API}/team/invites/accept/${inviteToken}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Welcome to the team!');
      if (res.data.co_organizer) {
        setCoResult(res.data);
      } else {
        navigate('/team');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to accept invite');
    } finally {
      setAccepting(false);
    }
  }, [inviteToken, token, navigate]);

  const sendEndorsement = async () => {
    const eventId = coResult?.event_ids?.[0];
    const emails = blast.emails.split(/[\n,;]+/).map(s => s.trim()).filter(s => s.includes('@'));
    if (!emails.length) { toast.error('Paste at least one email'); return; }
    setBlast(b => ({ ...b, sending: true }));
    try {
      const r = await axios.post(`${API}/team/events/${eventId}/endorsement`,
        { emails, note: blast.note }, { headers: { Authorization: `Bearer ${token}` } });
      setBlast(b => ({ ...b, sending: false, sent: r.data.sent }));
      toast.success(`Endorsement email sent to ${r.data.sent} people`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
      setBlast(b => ({ ...b, sending: false }));
    }
  };

  // Auto-accept if logged in with matching email
  useEffect(() => {
    if (isAuthenticated && invite?.status === 'pending' && user?.email?.toLowerCase() === invite.email?.toLowerCase()) {
      accept();
    }
  }, [isAuthenticated, invite, user, accept]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading invite…</div>;

  if (coResult) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-10" data-testid="co-organizer-welcome">
        <Card className="max-w-lg w-full">
          <CardContent className="py-8 space-y-4">
            <div className="text-center space-y-1">
              <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500" />
              <h2 className="text-xl font-bold">You're a Co-Organizer!</h2>
              <p className="text-sm text-muted-foreground">
                You now have full access to the event dashboard and you're listed publicly as a co-organizer.
                {coResult.followers_notified > 0 && ` We just emailed your ${coResult.followers_notified} followers about the event.`}
              </p>
            </div>
            {blast.sent == null ? (
              <div className="space-y-2 rounded-lg border border-border p-3" data-testid="endorsement-composer">
                <p className="text-sm font-semibold">New to Eventism? Tell your people.</p>
                <p className="text-xs text-muted-foreground">Paste your email list (one per line or comma-separated, up to 300). We'll send a one-time endorsement email promoting the event and inviting them to follow you here.</p>
                <textarea rows={4} className="w-full rounded-md border border-border bg-background p-2 text-sm"
                  placeholder={"jane@example.com\nmike@example.com"} value={blast.emails}
                  onChange={e => setBlast(b => ({ ...b, emails: e.target.value }))} data-testid="endorsement-emails-input" />
                <input className="w-full rounded-md border border-border bg-background p-2 text-sm"
                  placeholder="Optional personal note (shown in the email)" value={blast.note}
                  onChange={e => setBlast(b => ({ ...b, note: e.target.value }))} data-testid="endorsement-note-input" />
                <Button className="w-full bg-indigo-600 hover:bg-indigo-700" disabled={blast.sending} onClick={sendEndorsement} data-testid="endorsement-send-btn">
                  {blast.sending ? 'Sending…' : 'Send my endorsement email'}
                </Button>
              </div>
            ) : (
              <p className="text-sm text-center text-emerald-500 font-semibold" data-testid="endorsement-sent">Endorsement sent to {blast.sent} people 🎉</p>
            )}
            <div className="grid sm:grid-cols-2 gap-2">
              <Link to={`/event/${coResult.event_ids?.[0]}/manage`}>
                <Button className="w-full" variant="outline" data-testid="co-organizer-dashboard-btn">Open event dashboard</Button>
              </Link>
              <Link to="/team">
                <Button className="w-full" variant="ghost">Team Portal</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-10 text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-destructive" />
            <h2 className="text-xl font-semibold mb-2">Invite not found</h2>
            <p className="text-sm text-muted-foreground mb-6">This invitation link may be invalid or expired.</p>
            <Link to="/" className="text-sm text-indigo-600 hover:underline">Go to homepage</Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (invite.status !== 'pending') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-10 text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
            <h2 className="text-xl font-semibold mb-2 capitalize">Invite {invite.status}</h2>
            <p className="text-sm text-muted-foreground mb-6">{invite.message || 'This invitation is no longer active.'}</p>
            <Link to="/team" className="text-sm text-indigo-600 hover:underline">Go to Team Portal</Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const emailMismatch = isAuthenticated && user?.email?.toLowerCase() !== invite.email?.toLowerCase();

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10" data-testid="accept-invite-page">
      <Card className="max-w-lg w-full">
        <CardHeader>
          <div className="flex items-center gap-2 mb-2">
            <Mail className="w-5 h-5 text-indigo-600" />
            <Badge variant="outline">Team Invitation</Badge>
          </div>
          <CardTitle className="text-2xl">You've been invited!</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="p-4 rounded-lg bg-muted/40 text-sm space-y-2">
            <div><span className="text-muted-foreground">Invited by:</span> <strong>{invite.inviter_name}</strong></div>
            <div><span className="text-muted-foreground">Role:</span> <Badge>{invite.role_label}</Badge></div>
            <div><span className="text-muted-foreground">Email:</span> {invite.email}</div>
            <div className="pt-2">
              <div className="text-xs text-muted-foreground mb-1">You'll be able to:</div>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(invite.permissions || {}).filter(([, v]) => v).map(([k]) => (
                  <Badge key={k} variant="secondary" className="text-xs">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    {k.replace(/_/g, ' ')}
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          {emailMismatch && (
            <div className="p-3 rounded-lg border border-destructive/50 bg-destructive/10 text-sm text-destructive" data-testid="email-mismatch-warning">
              This invite was sent to <strong>{invite.email}</strong>. Please log out and sign in with that email.
            </div>
          )}

          {isAuthenticated && !emailMismatch && (
            <Button onClick={accept} disabled={accepting} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="accept-invite-btn">
              {accepting ? 'Accepting…' : 'Accept & Join Team'}
            </Button>
          )}

          {!isAuthenticated && (
            <div className="grid sm:grid-cols-2 gap-3">
              <Link to={`/register?returnTo=${encodeURIComponent(`/team/invite/${inviteToken}`)}&email=${encodeURIComponent(invite.email)}`}>
                <Button className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="accept-signup-btn">
                  <UserPlus className="w-4 h-4 mr-2" /> Create account
                </Button>
              </Link>
              <Link to={`/login?returnTo=${encodeURIComponent(`/team/invite/${inviteToken}`)}`}>
                <Button variant="outline" className="w-full" data-testid="accept-login-btn">
                  <LogIn className="w-4 h-4 mr-2" /> I have an account
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
